// src/services/logger.js
import { store } from "../redux/store";
import { addLog, sendLog } from "../redux/features/loggerSlice";

class LoggerService {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === "development";
    this.offlineMode = false;

    this.setupOfflineDetection();
  }

  setupOfflineDetection() {
    if (typeof window !== "undefined") {
      window.addEventListener("online", () => {
        this.offlineMode = false;
        this.retryPendingLogs();
      });

      window.addEventListener("offline", () => {
        this.offlineMode = true;
      });

      this.offlineMode = !navigator.onLine;
    }
  }

  async retryPendingLogs() {
    const state = store.getState();
    const { pendingLogs } = state.logger;

    for (const log of pendingLogs) {
      try {
        await store.dispatch(sendLog(log)).unwrap();
      } catch (error) {
        console.warn("Failed to retry log:", error);
        break;
      }
    }
  }

  // Método principal para logging
  log(level, message, additionalData = {}) {
    const logData = {
      level,
      message,
      ...additionalData,
    };

    // ✅ 1. Siempre agregar al store de Redux (esto automáticamente lo pone en pendingLogs)
    store.dispatch(addLog(logData));

    // ✅ 2. Mostrar en consola en desarrollo
    if (this.isDevelopment) {
      const consoleMethod = console[level] || console.log;
      consoleMethod(`[${level.toUpperCase()}]`, message, additionalData);
    }

    // ✅ 3. En desarrollo, no intentar enviar al servidor para evitar CORS
    if (this.isDevelopment) {
      console.log("📝 Log (almacenado localmente - modo desarrollo)");
      return;
    }

    // ✅ 4. En producción, intentar enviar inmediatamente si hay conexión
    if (!this.offlineMode) {
      // Preparamos los datos para el servidor (formato diferente)
      const serverLogData = this.prepareServerLogData(
        level,
        message,
        additionalData
      );

      store
        .dispatch(sendLog(serverLogData))
        .unwrap()
        .catch((error) => {
          console.warn("Error enviando log al servidor:", error);
          // El error ya es manejado por el slice, no need to do anything here
        });
    }
  }

  prepareServerLogData(level, message, additionalData = {}) {
    // Crear copia de additionalData sin los campos que van por separado
    const { file, line, stack, component, ...cleanAdditionalData } =
      additionalData;

    return {
      level,
      message,
      url: window.location.href,
      file: file || "",
      line: line || "",
      stack_trace: stack || "",
      additional_data: {
        component: component || "unknown",
        ...cleanAdditionalData,
      },
    };
  }

  error(error, additionalData = {}) {
    const logData = {
      file: additionalData.file,
      line: additionalData.line,
      stack: error.stack,
      component: additionalData.component,
      ...additionalData,
    };

    this.log("error", error.message, logData);
  }

  warning(message, additionalData = {}) {
    this.log("warning", message, additionalData);
  }

  info(message, additionalData = {}) {
    this.log("info", message, additionalData);
  }

  debug(message, additionalData = {}) {
    this.log("debug", message, additionalData);
  }
}

export const logger = new LoggerService();
